import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { PharmacyMapHandle } from 'src/features/pharmacies/shared/ui/PharmacyMap/constants';

import { FindPharmacyDdAction, PharmacyDdAction } from '@/constants/ddActions/pharmacy';
import { OutLinks } from '@/constants/url';
import { NonBenefitMapQueries, NonBenefitMedicineModel } from '@/entities/non-benefit';
import usePharmacyViewType from '@/features/pharmacies/shared/hooks/usePharmacyViewType';
import { PharmacyListBottomSheetHandle } from '@/features/pharmacies/shared/ui/PharmacyListBottomSheet/constants';
import useWebLocation from '@/hooks/common/useWebLocation';
import useFilteredAndSortedPharmacy from '@/hooks/pharmacy/useFilteredAndSortedPharmacy';
import useFindGeneralListOfNearbyPharmacyV3ByUser from '@/hooks/pharmacy/useFindGeneralListOfNearbyPharmacyV3ByUser';
import usePharmacySearchFilters from '@/hooks/pharmacy/usePharmacySearchFilters';
import useModal from '@/hooks/useModal';
import { PharmacyModel } from '@/models/pharmacy';
import { Client } from '@/types/app';
import { PharmacyFilterType, PharmacySortType } from '@/types/bff/gql-schema';
import { BreadCrumb, ROUTES } from '@/types/common';
import { UserCoordinate } from '@/types/hospital';
import { MODAL_TYPE } from '@/types/modal';
import { ENearbyPharmacySearchFilterType, ENearbyPharmacySearchSortType } from '@/types/pharmacy';
import Airbridge from '@/utils/airbridge';
import { addQueryString, updateSearchParams } from '@/utils/url';
import { DiagnosisAppInstallBottomSheet } from '@/views/non-benefit';

import { DOCTOR_NOW_COMPANY_LATITUDE, DOCTOR_NOW_COMPANY_LONGITUDE } from '@/constants/map';
import { EPixelEventName } from '@/constants/pixel';
import useGetNonBenefitDiagnosisPriceSupportInvitationCode from '@/entities/non-benefit/hooks/useGetNonBenefitDiagnosisPriceSupportInvitationCode';
import { NearbyPharmacyFilterCriteria } from '@/features/pharmacies/shared/hooks/usePharmacyFilterViewModel';
import Pixel from '@/utils/pixel/pixel';
import { useToast } from 'doctornow-design-system';
import useLoginViewModel from '../login/useLoginViewModel';
import useAddFavoritePharmacy from '../pharmacy/useAddFavoritePharmacy';
import useFavoritePharmacies from '../pharmacy/useFavoritePharmacies';
import { Tab } from '../pharmacy/useFindPharmacyCategoryTabs';
import useRemoveFavoritePharmacy from '../pharmacy/useRemoveFavoritePharmacy';
import useLogger from '../useLogger';
import useNavigate from '../useNavigate';
import useToaster from '../useToaster';
import useNonBenefitMapCategoryTabs from './useNonBenefitMapCategoryTabs';

const airbrigdeLink = 'https://abr.ge/w3tbbv';

const useNonBenefitMapViewModel = (client: Client) => {
  const router = useRouter();
  const navigate = useNavigate();
  const queries = router.query as NonBenefitMapQueries;
  const { tabs, selectedTab, selectTab, selectedTreatmentMedicine, selectedTreatmentMedicineUnit } =
    useNonBenefitMapCategoryTabs();
  const { toast } = useToast();

  const mapRef = useRef<PharmacyMapHandle>(null);
  const pharmacyListBottomSheetRef = useRef<PharmacyListBottomSheetHandle>(null);

  const { showModal, closeModal } = useModal();

  const breadCrumbs: BreadCrumb[] = [
    {
      label: '최저가 약국 찾기',
      url: ROUTES.NON_BENEFIT.MAP.path + addQueryString(queries),
      logName: 'non_benefit_map',
    },
  ];

  const scrollListRef = useRef<HTMLDivElement>(null);
  const userLocationHook = useWebLocation();

  const [selectedPharmacy, setSelectedPharmacy] = useState<PharmacyModel | null>(null);
  const [pharmacyMapInitiateError, setPharmacyMapInitiateError] = useState<ErrorEvent | null>(null);
  const [pharmacyFilterCriteria, setPharmacyFilterCriteria] = useState<NearbyPharmacyFilterCriteria | null>(null);

  const queryClient = useQueryClient();

  const { addFavoritePharmacyMutation } = useAddFavoritePharmacy();
  const { removeFavoritePharmacyMutation } = useRemoveFavoritePharmacy();
  const { invitationCode } = useGetNonBenefitDiagnosisPriceSupportInvitationCode();

  const { showToast } = useToaster();

  const { log } = useLogger();

  const loginViewModel = useLoginViewModel();

  const [showLoginModal, setShowLoginModal] = useState(false);

  const openLoginModal = () => {
    setShowLoginModal(true);
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
  };

  const shouldShowPharmacyCard = !!selectedPharmacy;

  const updateSelectedPharmacy = (pharmacy: PharmacyModel) => {
    Pixel.trackCustom(EPixelEventName.SelectPharmacy, {
      drug: selectedMedicine.medicineDisplayName,
    });

    setSelectedPharmacy(pharmacy);
    if (pharmacyListBottomSheetRef.current) {
      pharmacyListBottomSheetRef.current?.setInitialSnapPoint();
    }
  };

  const resetSelectedPharmacy = () => {
    setSelectedPharmacy(null);
    if (pharmacyListBottomSheetRef.current) {
      pharmacyListBottomSheetRef.current?.setInitialSnapPoint();
    }
  };

  const updatePharmacyMapInitiateError = (error: ErrorEvent) => {
    setPharmacyMapInitiateError(error);
  };

  const pharmacyViewTypeHook = usePharmacyViewType();

  const searchFiltersHook = usePharmacySearchFilters({
    nearbyPharmacySortsAndFilters: {
      filters: selectedTab?.filters ?? [],
      sorts: selectedTab?.sortTypes ?? [],
    },
    defaultSortType: selectedTab?.sortTypes[0].type ?? ENearbyPharmacySearchSortType.LowPrice,
  });

  const {
    data: rawPharmacyList,
    isLoading: isNearbyPharmaciesLoading,
    isVisibleMedicinePrice,
  } = useFindGeneralListOfNearbyPharmacyV3ByUser({
    ...searchFiltersHook.getBoundaryEastWestLongitudeAndLatitude(!!pharmacyMapInitiateError),
    latitude: searchFiltersHook.mapCoordinates.latitude,
    longitude: searchFiltersHook.mapCoordinates.longitude,
    diagnosisType: pharmacyFilterCriteria?.selectedTab?.diagnosisType,
    diagnosisTypeId: pharmacyFilterCriteria?.selectedTab?.diagnosisTypeId,
    treatmentMedicineId: pharmacyFilterCriteria?.selectedMedicine?.treatmentMedicineId,
    dispensationUnit: pharmacyFilterCriteria?.selectedMedicine?.dispensationUnit?.dispensationUnit,
    treatmentMedicineStrengthId: pharmacyFilterCriteria?.selectedMedicine?.treatmentMedicineStrengthId,
    shouldIncludeDiagnosisPrice:
      (pharmacyFilterCriteria?.selectedTab?.filters?.some(
        (filter) => filter.type === ENearbyPharmacySearchFilterType.IncludeDiagnosisPrice
      ) &&
        pharmacyFilterCriteria?.includeDiagnosisPriceFilter?.isSelectedFilter(
          pharmacyFilterCriteria?.selectedCommonFilters ?? []
        )) ??
      false,
  });

  const { refinedPharmacy: pharmacies, isDataReady: isDataReady } = useFilteredAndSortedPharmacy({
    filters: (pharmacyFilterCriteria?.selectedCommonFilters ?? []) as unknown as PharmacyFilterType[], // TODO. 왜 이런식으로 캐스팅을? 변경하기
    sortType: pharmacyFilterCriteria?.selectedSortMethod.type as unknown as PharmacySortType,
    pharmacyQueryData: {
      isLoading: isNearbyPharmaciesLoading,
      rawPharmacyData: rawPharmacyList ?? [],
    },
    shouldFilterByPriceMedicine: !!pharmacyFilterCriteria?.selectedMedicine?.treatmentMedicine.treatmentMedicineId,
  });

  const shouldShowEmptyPharmacyMessage = () => {
    if (isNearbyPharmaciesLoading || !isDataReady) {
      return false;
    }

    if (pharmacies.length === 0) {
      return true;
    }

    return false;
  };

  const parseSelectNonBenefitMedicine = (): NonBenefitMedicineModel => {
    const defaultValue = {
      id: selectedTreatmentMedicine.treatmentMedicineId,
      medicineName: selectedTreatmentMedicine.name,
      medicineCode: selectedTreatmentMedicine.medicineCode ?? '',
      medicineDescription: selectedTreatmentMedicine.description ?? '',
      units: [],
      treatment: {
        id: selectedTab.diagnosisTypeId ?? '',
        name: selectedTab.name,
        isBenefit: false,
        categoryId: '',
      },
    };

    return new NonBenefitMedicineModel(defaultValue);
  };

  const onClickViewListModeButtonWithResetSelectedPharmacy = () => {
    resetSelectedPharmacy();
    pharmacyListBottomSheetRef.current?.clickViewListModeButton();
  };

  const selectedMedicine: NonBenefitMedicineModel = parseSelectNonBenefitMedicine();

  const onClickPharmacyItem = (selectPharmacy: PharmacyModel) => {
    if (pharmacyMapInitiateError) {
      return;
    }

    setSelectedPharmacy(selectPharmacy);
  };

  const scrollToTop = () => {
    scrollListRef.current?.scrollTo({ top: 0 });
  };

  const applyFilter = (filterCriteria: NearbyPharmacyFilterCriteria) => {
    scrollToTop();
    setPharmacyFilterCriteria(filterCriteria);

    updateSearchParams([
      {
        key: 'treatmentMedicineId',
        value: filterCriteria.selectedMedicine?.treatmentMedicine.treatmentMedicineId ?? '',
      },
      {
        key: 'treatmentMedicineUnitId',
        value: filterCriteria.selectedMedicine?.dispensationUnit.treatmentMedicineUnitId ?? '',
      },
      {
        key: 'treatmentMedicineStrengthId',
        value: filterCriteria.selectedMedicine?.treatmentMedicineStrengthId ?? '',
      },
    ]);
  };

  const [currentMapLocation, setCurrentMapLocation] = useState<{ latitude: number; longitude: number }>({
    latitude: DOCTOR_NOW_COMPANY_LATITUDE,
    longitude: DOCTOR_NOW_COMPANY_LONGITUDE,
  });

  const updateCurrentMapCoordinate = (southWest: UserCoordinate, northEast: UserCoordinate) => {
    searchFiltersHook.updateMapCoordinate(southWest, northEast);

    const mapCenterLatLng = mapRef.current?.getCenterLatLng();

    const latitude = mapCenterLatLng?.latitude;
    const longitude = mapCenterLatLng?.longitude;

    if (!latitude || !longitude) {
      return;
    }

    setCurrentMapLocation({
      latitude,
      longitude,
    });
  };

  const isSelectedAllTab = () => {
    // 전체 탭의 세부 정보 (서버에서 내려옴)
    const ALL_TAB_INFO = {
      name: '전체',
      diagnosisTypeId: null,
      diagnosisType: null,
    };

    const isSameDiagnosisTypeWithAllTab =
      pharmacyFilterCriteria?.selectedTab?.diagnosisType === ALL_TAB_INFO.diagnosisType;
    const isSameDiagnosisTypeIdWithAllTab =
      pharmacyFilterCriteria?.selectedTab?.diagnosisTypeId === ALL_TAB_INFO.diagnosisTypeId;
    const isSameNameWithAllTab = pharmacyFilterCriteria?.selectedTab?.name === ALL_TAB_INFO.name;

    return (isSameDiagnosisTypeWithAllTab && isSameDiagnosisTypeIdWithAllTab && isSameNameWithAllTab) ?? false;
  };

  const getFavoritePharmacyClickEventName = (isFavorite: boolean) => {
    const viewType = pharmacyViewTypeHook.shouldShowListView ? 'list' : 'map';
    if (isFavorite) {
      return `${PharmacyDdAction.ClickRemoveFavoritePharmacy}_find_${viewType}`;
    }
    return `${PharmacyDdAction.ClickAddFavoritePharmacy}_find_${viewType}`;
  };

  const onClickOpenAppInstallLink = () => {
    Airbridge.setDeepLink({
      buttonID: 'pharmacy-map',
      deeplinkUrl: `${airbrigdeLink}${invitationCode ? `?invitationCode=${invitationCode}` : ''}`,
      ctaParams: {
        cta_param_1: '최저가 약국 지도',
        cta_param_2: selectedMedicine.medicineName,
        cta_param_3: selectedMedicine.treatment.name,
      },
    });
  };

  const onClickCopyInvitationCode = () => {
    if (navigator.clipboard) {
      toast({ text: '복사가 완료되었습니다' });
      navigator.clipboard.writeText(invitationCode ?? '');
    }
  };

  const onOpenAppInstallBottomSheet = () => {
    Pixel.trackCustom(EPixelEventName.BottomCtaClick, {
      drug: selectedMedicine.medicineDisplayName,
    });

    showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <DiagnosisAppInstallBottomSheet
            client={client}
            buttonId={'pharmacy-map'}
            treatmentProductMedicineName={selectedMedicine.treatmentProductMedicineName}
            invitationCode={invitationCode}
            onClose={closeModal}
            qrCodeUrl={`${airbrigdeLink}${invitationCode ? `?invitationCode=${invitationCode}` : ''}`}
            onClickOpenAppInstallLink={onClickOpenAppInstallLink}
            onClickCopyInvitationCode={onClickCopyInvitationCode}
          />
        ),
      },
    });
  };

  const onClickTab = (tab: Tab) => {
    selectTab(tab);
    resetSelectedPharmacy();
  };

  const addFavoritePharmacy = (pharmacy: PharmacyModel) => {
    addFavoritePharmacyMutation(
      {
        pharmacyId: pharmacy.pharmacyId,
      },
      {
        onSuccess: () => {
          showToast('단골 약국으로 지정했어요.\n더보기 메뉴에서 확인할 수 있어요.');
          pharmacy.updateFavoritePharmacy(true);
          // 단골 약국 캐시 업데이트
          useFavoritePharmacies.updateFavoritePharmacyStatus(queryClient, pharmacy.pharmacyId, true);
        },
        onError: (error) => {
          showToast(error.message);
        },
      }
    );
  };

  const removeFavoritePharmacy = (pharmacy: PharmacyModel) => {
    removeFavoritePharmacyMutation(
      {
        pharmacyId: pharmacy.pharmacyId,
      },
      {
        onSuccess: () => {
          showToast('단골 약국에서 제외했어요.');
          pharmacy.updateFavoritePharmacy(false);
          // 단골 약국 캐시 업데이트
          useFavoritePharmacies.updateFavoritePharmacyStatus(queryClient, pharmacy.pharmacyId, false);
        },
      }
    );
  };

  const onClickFavoritePharmacy = (e: React.MouseEvent, pharmacy: PharmacyModel) => {
    e.stopPropagation();

    if (!client?.isWebView && !loginViewModel.isLoggedIn) {
      openLoginModal();
      return;
    }

    if (pharmacy.isFavoritePharmacy) {
      return removeFavoritePharmacy(pharmacy);
    }
    addFavoritePharmacy(pharmacy);
  };

  const openNowPharmacyInfo = () => {
    log({ eventName: `${FindPharmacyDdAction.ClickNowInfoInFind}` });

    navigate.navigateToPureWebViewAppScreen({
      uri: OutLinks.StockConfirmInfo,
    });
  };

  const onClickCurrentLocation = async () => {
    // useWebLocation을 이용한 구현
    userLocationHook.requestGeolocationPermissionInWeb(({ latitude, longitude }) => {
      if (pharmacyMapInitiateError) {
        return;
      }

      mapRef.current?.setCenter(latitude, longitude);
    }, true);
  };

  useEffect(() => {
    if (!selectedMedicine.medicineDisplayName) {
      return;
    }

    Pixel.trackCustom(EPixelEventName.PageIn, {
      drug: selectedMedicine.medicineDisplayName,
    });
  }, [selectedMedicine.medicineDisplayName]);

  return {
    breadCrumbs,
    ...userLocationHook,
    ...searchFiltersHook,
    isNearbyPharmaciesLoading,
    pharmacies,
    scrollListRef,
    selectedTreatmentMedicine,
    selectedTreatmentMedicineUnit,
    queries,
    selectedMedicine,
    currentMapLocation,
    selectedPharmacy,
    mapRef,
    pharmacyListBottomSheetRef,
    tabs,
    selectedTab,
    selectedTabName: selectedTab?.name ?? '탈모약',
    shouldShowPharmacyCard,
    selectTab,
    onClickTab,
    shouldShowEmptyPharmacyMessage,
    updateCurrentMapCoordinate,
    applyFilter,
    updateSelectedPharmacy,
    resetSelectedPharmacy,
    updatePharmacyMapInitiateError,
    onClickPharmacyItem,
    isSelectedAllTab,
    onOpenAppInstallBottomSheet,
    getFavoritePharmacyClickEventName,
    onClickViewListModeButtonWithResetSelectedPharmacy,
    openNowPharmacyInfo,
    onClickFavoritePharmacy,
    openLoginModal,
    closeLoginModal,
    showLoginModal,
    onClickCurrentLocation,
    isVisibleMedicinePrice,
  };
};

export default useNonBenefitMapViewModel;
